import { CANADIAN_TIME_ZONES } from '@clairo/domain';
import { Link } from '@tanstack/react-router';
import {
  ArrowUpRight,
  Building2,
  Check,
  Fingerprint,
  GitBranch,
  Lock,
  Palette,
  ShieldCheck,
  UserRound,
} from 'lucide-react';
import { animate, motion, useMotionValue, useTransform } from 'motion/react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { addPasskey, authErrorKey } from '../auth/cognito';
import { useMe, useOrgUnits, useSession } from '../auth/session';
import { Skeleton } from '../components/feedback/Progress';
import { toast } from '../components/feedback/toast';
import { Badge, Card } from '../components/ui/Card';
import { cn } from '../lib/cn';
import { useTheme } from '../theme/ThemeProvider';

const stagger = (i: number) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { delay: 0.05 + i * 0.06, duration: 0.45, ease: [0.16, 1, 0.3, 1] as const },
});

export function HomePage() {
  const { t, i18n } = useTranslation();
  const { data: me } = useMe();
  const { org, membership } = useSession();
  const units = useOrgUnits(org);

  const now = new Date();
  const hour = now.getHours();
  const part = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const zoneName =
    CANADIAN_TIME_ZONES.find((z) => z.zone === zone)?.[
      i18n.language.startsWith('fr') ? 'fr' : 'en'
    ] ?? zone;
  const firstName = me?.user.displayName.split(' ')[0] ?? '';

  return (
    <div className="mx-auto flex max-w-[1600px] flex-col gap-6">
      <motion.header {...stagger(0)} className="flex flex-col gap-1">
        <h1 className="text-3xl font-extrabold text-text sm:text-4xl">
          {t(`home.greeting.${part}`, { name: firstName })}{' '}
          <motion.span
            className="inline-block origin-[70%_70%]"
            animate={{ rotate: [0, 16, -8, 14, 0] }}
            transition={{ duration: 1.6, delay: 0.6 }}
            aria-hidden="true"
          >
            👋
          </motion.span>
        </h1>
        <p className="text-md text-muted">
          {t('home.today', {
            date: new Intl.DateTimeFormat(i18n.language, { dateStyle: 'full' }).format(now),
            zone: zoneName,
          })}
        </p>
      </motion.header>

      {me && me.memberships.length === 0 ? (
        <motion.div {...stagger(1)}>
          <Card className="relative overflow-hidden p-8">
            <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-glow-1/30 blur-3xl" />
            <Badge tone="accent">{t('roles.platform_admin')}</Badge>
            <h2 className="mt-3 text-2xl font-bold text-text">{t('home.platform.title')}</h2>
            <p className="mt-2 max-w-xl text-md text-muted">{t('home.platform.body')}</p>
          </Card>
        </motion.div>
      ) : (
        <>
          <section className="grid grid-cols-2 gap-4 xl:grid-cols-4">
            <Stat
              i={1}
              icon={<Building2 size={18} />}
              label={t('home.stats.orgUnits')}
              loading={units.isLoading}
            >
              <CountUp value={units.data?.length ?? 0} />
            </Stat>
            <Stat
              i={2}
              icon={<GitBranch size={18} />}
              label={t('home.stats.branches')}
              loading={units.isLoading}
            >
              <CountUp value={units.data?.filter((u) => u.type === 'branch').length ?? 0} />
            </Stat>
            <Stat i={3} icon={<UserRound size={18} />} label={t('home.stats.role')}>
              <span className="text-xl">{membership ? t(`roles.${membership.role}`) : '—'}</span>
            </Stat>
            <Stat i={4} icon={<ShieldCheck size={18} />} label={t('home.stats.security')}>
              <span className={cn('text-xl', me?.mfa.enrolled ? 'text-success' : 'text-warning')}>
                {me?.mfa.enrolled ? t('home.stats.mfaOn') : t('home.stats.mfaOff')}
              </span>
            </Stat>
          </section>

          <section className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
            <motion.div {...stagger(5)}>
              <Checklist />
            </motion.div>
            <motion.div {...stagger(6)}>
              <Card className="h-full p-6">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-lg font-bold text-text">{t('home.orgCard.title')}</h2>
                  <Link
                    to="/organization"
                    className="flex items-center gap-1 text-sm font-bold text-primary hover:underline"
                  >
                    {t('home.orgCard.manage')} <ArrowUpRight size={15} />
                  </Link>
                </div>
                {units.isLoading ? (
                  <div className="flex flex-col gap-2.5">
                    {[0, 1, 2, 3].map((i) => (
                      <Skeleton key={i} className="h-11" />
                    ))}
                  </div>
                ) : (
                  <ul className="flex flex-col gap-1.5">
                    {(units.data ?? []).slice(0, 7).map((u, i) => (
                      <motion.li
                        key={u.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.25 + i * 0.05 }}
                        className="flex items-center gap-3 rounded-md px-3 py-2.5 hover:bg-subtle"
                      >
                        <span className="grid h-8 w-8 place-items-center rounded-md bg-primary/10 text-primary">
                          <Building2 size={15} />
                        </span>
                        <span className="flex-1 truncate font-semibold text-text">{u.name}</span>
                        <Badge>{t(`org.types.${u.type}`, { defaultValue: u.type })}</Badge>
                      </motion.li>
                    ))}
                  </ul>
                )}
              </Card>
            </motion.div>
          </section>
        </>
      )}
    </div>
  );
}

function Stat({
  i,
  icon,
  label,
  loading,
  children,
}: {
  i: number;
  icon: ReactNode;
  label: string;
  loading?: boolean;
  children: ReactNode;
}) {
  return (
    <motion.div {...stagger(i)}>
      <Card interactive className="relative overflow-hidden p-5">
        <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-glow-1/15 blur-2xl" />
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-muted">
          <span className="grid h-8 w-8 place-items-center rounded-md bg-subtle text-primary">
            {icon}
          </span>
          {label}
        </div>
        <div className="tabular font-display text-3xl font-extrabold text-text">
          {loading ? <Skeleton className="h-9 w-16" /> : children}
        </div>
      </Card>
    </motion.div>
  );
}

function CountUp({ value }: { value: number }) {
  const count = useMotionValue(0);
  const rounded = useTransform(count, (v) => Math.round(v).toString());
  useEffect(() => {
    const controls = animate(count, value, { duration: 1.1, ease: [0.16, 1, 0.3, 1] });
    return () => controls.stop();
  }, [count, value]);
  return <motion.span>{rounded}</motion.span>;
}

const PASSKEY_KEY = 'clairo.passkey';

function Checklist() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const { choice } = useTheme();
  const [passkey, setPasskey] = useState(() => localStorage.getItem(PASSKEY_KEY) === '1');
  const [adding, setAdding] = useState(false);

  const items: {
    key: string;
    icon: ReactNode;
    done: boolean;
    action?: ReactNode;
    soon?: boolean;
  }[] = [
    {
      key: 'mfa',
      icon: <ShieldCheck size={16} />,
      done: Boolean(me?.mfa.enrolled),
      action: (
        <Link to="/security/mfa" className="text-sm font-bold text-primary hover:underline">
          →
        </Link>
      ),
    },
    {
      key: 'passkey',
      icon: <Fingerprint size={16} />,
      done: passkey,
      action: (
        <button
          disabled={adding}
          className="text-sm font-bold text-primary hover:underline disabled:opacity-60"
          onClick={() => {
            setAdding(true);
            const id = toast.loading(t('mfa.passkey.adding'));
            addPasskey()
              .then(() => {
                localStorage.setItem(PASSKEY_KEY, '1');
                setPasskey(true);
                toast.success(t('mfa.passkey.added'), { id });
              })
              .catch((err: unknown) => toast.error(t(authErrorKey(err)), { id }))
              .finally(() => setAdding(false));
          }}
        >
          {t('mfa.passkey.action')}
        </button>
      ),
    },
    {
      key: 'theme',
      icon: <Palette size={16} />,
      done: choice !== 'system',
      action: (
        <Link to="/appearance" className="text-sm font-bold text-primary hover:underline">
          →
        </Link>
      ),
    },
    { key: 'invite', icon: <UserRound size={16} />, done: false, soon: true },
    { key: 'clients', icon: <Lock size={16} />, done: false, soon: true },
  ];
  const done = items.filter((i) => i.done).length;
  const ratio = done / items.length;

  return (
    <Card className="h-full p-6">
      <div className="mb-5 flex items-center gap-4">
        <ProgressRing ratio={ratio} />
        <div>
          <h2 className="text-lg font-bold text-text">{t('home.checklist.title')}</h2>
          <p className="text-sm text-muted">
            {t('home.checklist.progress', { done, total: items.length })}
          </p>
        </div>
      </div>
      <ul className="flex flex-col gap-1">
        {items.map((item) => (
          <li key={item.key} className="flex items-center gap-3 rounded-md px-2 py-2.5">
            <motion.span
              animate={item.done ? { scale: [1, 1.25, 1] } : {}}
              className={cn(
                'grid h-7 w-7 shrink-0 place-items-center rounded-full border-2',
                item.done ? 'border-success bg-success text-white' : 'border-border text-muted',
              )}
            >
              {item.done ? <Check size={14} strokeWidth={3} /> : item.icon}
            </motion.span>
            <span
              className={cn(
                'min-w-0 flex-1 text-base',
                item.done ? 'text-muted line-through decoration-2' : 'font-semibold text-text',
              )}
            >
              {t(`home.checklist.${item.key}`)}
            </span>
            {item.soon ? <Badge>{t('common.comingSoon')}</Badge> : !item.done && item.action}
          </li>
        ))}
      </ul>
    </Card>
  );
}

function ProgressRing({ ratio }: { ratio: number }) {
  const r = 22;
  const c = 2 * Math.PI * r;
  return (
    <svg width={56} height={56} viewBox="0 0 56 56" className="-rotate-90" aria-hidden="true">
      <circle cx={28} cy={28} r={r} fill="none" stroke="var(--subtle)" strokeWidth={6} />
      <motion.circle
        cx={28}
        cy={28}
        r={r}
        fill="none"
        stroke="url(#ring)"
        strokeWidth={6}
        strokeLinecap="round"
        strokeDasharray={c}
        initial={{ strokeDashoffset: c }}
        animate={{ strokeDashoffset: c * (1 - ratio) }}
        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
      />
      <defs>
        <linearGradient id="ring" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="var(--primary)" />
          <stop offset="100%" stopColor="var(--accent)" />
        </linearGradient>
      </defs>
    </svg>
  );
}
